/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * The v2 automation builder (design reference "Automations · Builder"): a
 * palette of steps on the left, the flow on a dotted canvas in the middle,
 * and the selected step's settings on the right.
 *
 * It runs on the wizard's own state (AutomationProvider), forms, validation
 * and save (transformService). Waits are drawn as steps (canvasModel.js). A
 * flow with Yes/No branches or business-day waits is saved as the engine's
 * `flow`; a plain one stays flat actions, exactly as the wizard saves it.
 *
 * Branches, the "On a schedule" trigger, Test run and editing with Seep need
 * the flow engine (backend automation_flow / automation_schedule /
 * automation_edits). Until the backend has it they stay "coming next": a
 * branched flow sent to an older backend would be saved as plain actions and
 * run every step.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Snackbar from "@mui/material/Snackbar";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { ArrowLeft, CalendarClock, Check, Clock, Play, Split, Zap } from "lucide-react";
import { useAuth } from "auth/Auth";
import MDButton from "components/MDButton";
import PageHeader from "components/v2/PageHeader";
import { v2 as t } from "styles/tokens/v2";
import { AutomationProvider, useAutomationContext } from "components/AutomationWizard/context/AutomationContext";
import { useAutomationMetadata } from "components/AutomationWizard/hooks/useAutomationMetadata";
import transformService from "components/AutomationWizard/services/transformService";
import validationService from "components/AutomationWizard/services/validationService";
import ActionCard from "components/AutomationWizard/components/ActionCard";
import useAiAssistantStatus from "hooks/useAiAssistantStatus";
import { relativeTime } from "layouts/crm/account360/format";
import {
  MAX_STEPS,
  actionsToSteps,
  allActions,
  allSteps,
  cloneStep,
  describeAction,
  describeConditions,
  describeMinutes,
  describeTrigger,
  findStep,
  flowToSteps,
  getList,
  insertAt,
  mapConditionGroups,
  minutesToDelay,
  moveDeep,
  newActionStep,
  newConditionStep,
  newWaitStep,
  removeDeep,
  savePayload,
  stepsToFlow,
  tooDeep,
  trailingWait,
  updateDeep,
} from "./canvasModel";
import { Connector, DropZone, FlowCard, FlowChip, Palette } from "./CanvasPieces";
import CanvasViewport from "./CanvasViewport";
import ConditionEditor from "./ConditionEditor";
import LabeledField, { inputSx } from "./LabeledField";
import ScheduleEditor, { defaultSchedule, describeSchedule, scheduleForSave } from "./ScheduleEditor";
import SeepEditBox from "./SeepEditBox";
import TestRunDialog from "./TestRunDialog";
import TriggerEditor from "./TriggerEditor";
import { TONES, actionMeta } from "./stepKinds";

const SCHEDULE = "on schedule";
const SCHEDULE_TRIGGER = [{ id: SCHEDULE, name: "On a schedule" }];

const panelSx = { bgcolor: t.surface, border: `1px solid ${t.border}`, borderRadius: "12px" };

function WaitEditor({ minutes, businessDays, engineReady, onChange, onBusinessDays }) {
  const d = minutesToDelay(minutes);
  const set = (field, value) => {
    const next = { ...d, [field]: Math.max(0, parseInt(value, 10) || 0) };
    onChange(next.days * 1440 + next.hours * 60 + next.minutes);
  };
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography sx={{ fontSize: 14, color: t.text2 }}>Steps below this one run this long after the steps above it.</Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1 }}>
        {[["days", "Days"], ["hours", "Hours"], ["minutes", "Minutes"]].map(([field, label]) => (
          <LabeledField key={field} type="number" label={label} value={d[field]} inputProps={{ min: 0 }} onChange={(e) => set(field, e.target.value)} />
        ))}
      </Box>
      {engineReady ? (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: 14, color: t.text }}>Business days only</Typography>
            <Switch checked={Boolean(businessDays)} onChange={(e) => onBusinessDays(e.target.checked)} inputProps={{ "aria-label": "Business days only" }} />
          </Box>
          <Typography sx={{ fontSize: 13, color: t.text2 }}>
            {businessDays ? "Saturdays and Sundays don't count, in the automation owner's time zone." : "Counts every day. The run pauses here and picks up when the wait is over."}
          </Typography>
        </Box>
      ) : (
        <Tooltip title="Business days and time zones are coming next.">
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", opacity: 0.6 }}>
            <Typography sx={{ fontSize: 14, color: t.text }}>Business days only</Typography>
            <Switch disabled checked={false} inputProps={{ "aria-label": "Business days only (coming next)" }} />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
}
WaitEditor.propTypes = {
  minutes: PropTypes.number.isRequired,
  businessDays: PropTypes.bool,
  engineReady: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onBusinessDays: PropTypes.func,
};
WaitEditor.defaultProps = { businessDays: false, engineReady: false, onBusinessDays: () => {} };

function CanvasContent({ automation, onClose, onSave }) {
  const { state, actions } = useAutomationContext();
  const { metadata, getFieldsForObject, loadOperators } = useAutomationMetadata();
  const { axiosInstance } = useAuth();
  const [steps, setSteps] = useState([]);
  const [selected, setSelected] = useState("trigger");
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState(null);
  const [stats, setStats] = useState(null);
  const [schedule, setSchedule] = useState(defaultSchedule);
  const [engineReady, setEngineReady] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [tested, setTested] = useState(null); // step ids a test run went through
  const { canUseAiAssistant } = useAiAssistantStatus();
  const loaded = useRef(false);

  // Does the backend run flows? POST /automations/test answers 422 to an
  // empty body when it exists; older backends answer 404/405.
  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .post("/automations/test", {})
      .catch((e) => e?.response)
      .then((res) => !cancelled && setEngineReady(res?.status === 422));
    return () => {
      cancelled = true;
    };
  }, [axiosInstance]);

  useEffect(() => {
    if (automation) {
      const data = transformService.deserializeAutomation(automation);
      actions.loadAutomation(data);
      setSteps(automation.flow ? mapConditionGroups(flowToSteps(automation.flow), (g) => transformService.deserializeConditions(g)) : actionsToSteps(data.actions));
      setSchedule(automation.schedule ? { ...defaultSchedule(), ...automation.schedule } : defaultSchedule());
      setSelected(data.wizardData.object ? null : "trigger");
    } else {
      actions.resetWizard();
      setSteps([]);
      setSchedule(defaultSchedule());
      setSelected("trigger");
    }
    setTested(null);
    loaded.current = true;
    // Load once per automation shown.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [automation]);

  // The wizard state holds the actions (validated as the wizard validates
  // them); the canvas keeps the steps. In a branched flow that is every
  // action in the tree, in the order drawn.
  useEffect(() => {
    if (loaded.current) actions.setActions(savePayload(steps).actions);
    setTested(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps]);

  useEffect(() => {
    if (!automation?.id) return undefined;
    let cancelled = false;
    axiosInstance
      .get(`/automations/${automation.id}/stats`, { params: { days: 30 } })
      .then(({ data }) => !cancelled && setStats(data))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [automation?.id, axiosInstance]);

  const objectMeta = metadata?.objects?.[state.wizardData.object];
  const objectName = objectMeta?.name || state.wizardData.object;
  const fields = getFieldsForObject(state.wizardData.object);
  const conditionText = describeConditions(state.conditionGroups, state.groupLogic);

  const errorsByStep = useMemo(() => {
    const meta = { objectMetadata: state.objectMetadata, fieldMetadata: state.fieldMetadata, operators: state.operators };
    return [0, 1, 2].map((i) => validationService.validateStep(i, state, meta));
  }, [state]);
  const actionIndexOf = (id) => allActions(steps).findIndex((s) => s.id === id);
  const actionInvalid = (id) => {
    const i = actionIndexOf(id);
    return Object.keys(errorsByStep[2]?.errors || {}).some((k) => k.startsWith(`action_${i}_`) || k === `action_${i}`);
  };

  // Every change to the tree goes through here, so the limits the engine
  // enforces are explained on the canvas instead of failing on save.
  const commit = (next) => {
    if (next === steps) return false;
    if (allSteps(next).length > MAX_STEPS) {
      setSnack({ severity: "warning", message: `A flow can have at most ${MAX_STEPS} steps.` });
      return false;
    }
    if (tooDeep(next)) {
      setSnack({ severity: "warning", message: "Conditions can sit at most two levels deep." });
      return false;
    }
    setSteps(next);
    return true;
  };

  const addAt = (spec, index, path = []) => {
    if (spec.kind === "condition" && !engineReady) return;
    const step = spec.kind === "wait" ? newWaitStep() : spec.kind === "condition" ? newConditionStep() : newActionStep(spec.type);
    if (commit(insertAt(steps, path, index, step))) setSelected(step.id);
  };

  const handleDrop = (payload, index, path = []) => {
    if (payload.source === "canvas") {
      commit(moveDeep(steps, payload.id, path, index));
      return;
    }
    if (payload.source === "palette") addAt(payload, index, path);
  };

  const pickTrigger = (key) => {
    setSelected("trigger");
    if (key === "schedule") {
      if (engineReady) actions.updateWizardData({ trigger: SCHEDULE });
      return;
    }
    const available = objectMeta?.capabilities?.triggers || [];
    const match = available.find((tr) => String(tr).toLowerCase().includes(key));
    if (match) actions.updateWizardData({ trigger: match });
    else if (!state.wizardData.object) setSnack({ severity: "info", message: "Choose the record type first, then the event." });
  };

  const duplicate = (step) => {
    const at = findStep(steps, step.id);
    const copy = cloneStep(step);
    if (at && commit(insertAt(steps, at.path, at.index + 1, copy))) setSelected(copy.id);
  };

  const isScheduled = state.wizardData.trigger === SCHEDULE;
  // What is on the canvas, in the shape the API takes (saved or not).
  const currentAutomation = () => {
    const base = transformService.serializeAutomation(state);
    const { actions: flat, flow } = savePayload(mapConditionGroups(steps, (g) => transformService.serializeConditions(g)));
    return { ...base, actions: flat, flow, schedule: isScheduled ? scheduleForSave(schedule) : null };
  };

  const save = async () => {
    const failed = errorsByStep.findIndex((v) => !v.valid);
    if (failed >= 0) {
      actions.setErrors(errorsByStep[failed].errors);
      setSelected(failed === 0 ? "trigger" : failed === 1 ? "conditions" : steps.find((s) => s.kind === "action" && actionInvalid(s.id))?.id || null);
      setSnack({ severity: "error", message: Object.values(errorsByStep[failed].errors)[0] || "Check the highlighted step." });
      return;
    }
    if (!allActions(steps).length) {
      setSnack({ severity: "error", message: "Add at least one action." });
      return;
    }
    const emptyBranch = allSteps(steps).find((s) => s.kind === "condition" && !(s.groups || []).some((g) => (g.conditions || []).length));
    if (emptyBranch) {
      setSelected(emptyBranch.id);
      setSnack({ severity: "error", message: "Give the highlighted condition something to check." });
      return;
    }
    if (isScheduled && (!schedule.time || (schedule.frequency === "weekly" && !(schedule.weekdays || []).length))) {
      setSelected("trigger");
      setSnack({ severity: "error", message: "Finish the schedule: pick a time and, for weekly, at least one day." });
      return;
    }
    setSaving(true);
    try {
      const payload = currentAutomation();
      if (!engineReady) {
        // An older backend keeps only flat actions; nothing here needs more.
        delete payload.flow;
        delete payload.schedule;
      }
      await onSave(payload);
      setSnack({ severity: "success", message: "Automation saved." });
    } catch (error) {
      setSnack({ severity: "error", message: error.message || "Could not save the automation." });
    } finally {
      setSaving(false);
    }
  };

  const selectedStep = findStep(steps, selected)?.step;
  const editedBy = automation?.updated_at ? `Edited ${relativeTime(automation.updated_at)}${automation.updated_by_name ? ` by ${automation.updated_by_name}` : ""}` : "Not saved yet";

  // -------------------------------------------------------------- inspector
  let inspectorKind = null;
  let inspectorTitle = null;
  let inspectorBody = null;
  if (selected === "trigger") {
    inspectorKind = "Trigger";
    inspectorTitle = describeTrigger(objectName, state.wizardData.trigger);
    inspectorTitle = isScheduled ? `${describeSchedule(schedule)}` : inspectorTitle;
    inspectorBody = (
      <>
        <TriggerEditor objects={metadata?.objects} extraTriggers={engineReady ? SCHEDULE_TRIGGER : undefined} />
        {isScheduled && <ScheduleEditor schedule={schedule} onChange={setSchedule} nextRunAt={automation?.next_run_at || null} />}
      </>
    );
  } else if (selected === "conditions") {
    inspectorKind = "Condition";
    inspectorTitle = conditionText.title;
    inspectorBody = (
      <>
        <Typography sx={{ fontSize: 14, color: t.text2, mb: 2 }}>Records that don't match are skipped: none of the steps run for them.</Typography>
        <ConditionEditor
          groups={state.conditionGroups}
          groupLogic={state.groupLogic}
          fields={fields}
          loadOperators={loadOperators}
          emptyText={`Runs for every ${objectName ? objectName.toLowerCase() : "record"}. Add a condition to narrow it down.`}
          onChange={({ groups, groupLogic }) => {
            if (groups !== state.conditionGroups) actions.setConditionGroups(groups);
            if (groupLogic !== state.groupLogic) actions.setGroupLogic(groupLogic);
          }}
        />
      </>
    );
  } else if (selectedStep?.kind === "condition") {
    const text = describeConditions(selectedStep.groups, selectedStep.groupLogic);
    inspectorKind = "Yes / No";
    inspectorTitle = (selectedStep.groups || []).some((g) => (g.conditions || []).length) ? text.title : "Choose what to check";
    inspectorBody = (
      <>
        <Typography sx={{ fontSize: 14, color: t.text2, mb: 2 }}>
          Records that match go down Yes, the rest down No. After a wait, this checks the record as it is then.
        </Typography>
        <ConditionEditor
          groups={selectedStep.groups || []}
          groupLogic={selectedStep.groupLogic}
          fields={fields}
          loadOperators={loadOperators}
          emptyText="Add what to check. Until then every record goes down No."
          onChange={({ groups, groupLogic }) => setSteps((prev) => updateDeep(prev, selectedStep.id, { groups, groupLogic }))}
        />
      </>
    );
  } else if (selectedStep?.kind === "wait") {
    inspectorKind = "Wait";
    inspectorTitle = `Wait ${describeMinutes(selectedStep.minutes)}${selectedStep.businessDays ? " (business days)" : ""}`;
    inspectorBody = (
      <WaitEditor
        minutes={selectedStep.minutes}
        businessDays={selectedStep.businessDays}
        engineReady={engineReady}
        onChange={(m) => setSteps((prev) => updateDeep(prev, selectedStep.id, { minutes: m }))}
        onBusinessDays={(on) => setSteps((prev) => updateDeep(prev, selectedStep.id, { businessDays: on }))}
      />
    );
  } else if (selectedStep?.kind === "action") {
    inspectorKind = actionMeta(selectedStep.action.type).kindLabel;
    inspectorTitle = describeAction(selectedStep.action);
    inspectorBody = (
      <ActionCard
        key={selectedStep.id}
        variant="inspector"
        action={selectedStep.action}
        index={actionIndexOf(selectedStep.id)}
        onUpdate={(updates) => setSteps((prev) => updateDeep(prev, selectedStep.id, { action: { ...selectedStep.action, ...updates } }))}
        onRemove={() => setSteps((prev) => removeDeep(prev, selectedStep.id))}
      />
    );
  }

  // ------------------------------------------------------------ flow lists
  // One list of steps with its drop zones; a condition step draws its Yes
  // and No lists side by side, then the flow rejoins below.
  const branchLabel = (path) => {
    if (!path.length) return "";
    const branch = path[path.length - 1];
    return ` in the ${branch === "yes" ? "Yes" : "No"} branch`;
  };
  const renderList = (list, path) => {
    const nested = path.length > 0;
    const cardWidth = nested ? 260 : 340;
    const level = path.length / 2 + 1;
    const zone = (index) => (
      <DropZone
        index={index}
        where={`at position ${index + 1}${branchLabel(path)}`}
        allowCondition={engineReady && level < 3}
        onDrop={(payload, i) => handleDrop(payload, i, path)}
        onAdd={(spec, i) => addAt(spec, i, path)}
      />
    );
    return (
      <>
        {zone(0)}
        {list.map((step, i) => {
          const remove = () => {
            setSteps((prev) => removeDeep(prev, step.id));
            if (selected === step.id) setSelected(null);
          };
          if (step.kind === "condition") {
            const text = describeConditions(step.groups, step.groupLogic);
            const hasRules = (step.groups || []).some((g) => (g.conditions || []).length);
            return (
              <Box key={step.id} sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
                <FlowCard
                  icon={Split}
                  tone={TONES.condition}
                  kindLabel="Yes / No"
                  title={hasRules ? text.title.replace(/^Only when/, "If") : "Choose what to check"}
                  sub={hasRules ? text.sub : "Select to add conditions"}
                  width={cardWidth}
                  invalid={!hasRules && Boolean(state.showValidationErrors)}
                  selected={selected === step.id}
                  highlighted={Boolean(tested?.has(step.id))}
                  onSelect={() => setSelected(step.id)}
                  draggableId={step.id}
                  onDuplicate={() => duplicate(step)}
                  onDelete={remove}
                  onDropAfter={(payload) => handleDrop(payload, i + 1, path)}
                />
                <Connector />
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(280px, 1fr))", columnGap: 3, width: "100%", minWidth: 600 }}>
                  {["yes", "no"].map((branch) => (
                    <Box
                      key={branch}
                      component="section"
                      aria-label={`${branch === "yes" ? "Yes" : "No"} branch`}
                      sx={{ display: "flex", flexDirection: "column", alignItems: "center", borderTop: `2px solid ${branch === "yes" ? "#16A34A" : "#D97706"}`, pt: 1 }}
                    >
                      <FlowChip>{branch === "yes" ? "Yes" : "No"}</FlowChip>
                      {renderList(getList(steps, [...path, step.id, branch]) || [], [...path, step.id, branch])}
                    </Box>
                  ))}
                </Box>
                <Connector />
                {zone(i + 1)}
              </Box>
            );
          }
          const isWait = step.kind === "wait";
          const meta = isWait ? null : actionMeta(step.action.type);
          return (
            <Box key={step.id} sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
              <FlowCard
                icon={isWait ? Clock : meta.icon}
                tone={isWait ? TONES.wait : TONES.action}
                kindLabel={isWait ? "Wait" : meta.kindLabel}
                title={isWait ? `Wait ${describeMinutes(step.minutes)}` : step.action.type ? describeAction(step.action) : "Choose an action"}
                sub={isWait ? (step.businessDays ? "Business days only" : i === list.length - 1 ? "Nothing runs after this wait" : "Then the steps below run") : meta.label}
                width={cardWidth}
                invalid={!isWait && actionInvalid(step.id)}
                selected={selected === step.id}
                highlighted={Boolean(tested?.has(step.id))}
                onSelect={() => setSelected(step.id)}
                draggableId={step.id}
                onDuplicate={() => duplicate(step)}
                onDelete={remove}
                onDropAfter={(payload) => handleDrop(payload, i + 1, path)}
              />
              {zone(i + 1)}
            </Box>
          );
        })}
        {nested && !list.length && <Typography sx={{ fontSize: 12.5, color: t.text3, mb: 1 }}>Nothing happens on this side</Typography>}
      </>
    );
  };

  return (
    <Box>
      <PageHeader
        title={state.wizardData.name || (automation?.id ? "Automation" : "New automation")}
        summary={state.wizardData.object ? `Runs ${describeTrigger(objectName, state.wizardData.trigger).replace(/^When/, "when").replace(/^On a/, "on a")}` : "Choose what starts it, then drag steps into the flow."}
        actions={
          <>
            <MDButton variant="outlined" color="dark" startIcon={<ArrowLeft size={16} />} onClick={onClose}>
              Automations
            </MDButton>
            <Tooltip title={engineReady ? (state.wizardData.object ? "See what it would do to a record, without changing anything." : "Choose the record type first.") : "Test runs are coming next. Today a run uses real records."}>
              <span>
                <MDButton variant="outlined" color="dark" startIcon={<Play size={16} />} disabled={!engineReady || !state.wizardData.object || !allActions(steps).length} onClick={() => setTestOpen(true)}>
                  Test run
                </MDButton>
              </span>
            </Tooltip>
            <MDButton variant="gradient" color="info" startIcon={<Check size={16} />} onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </MDButton>
          </>
        }
      />

      {/* Status strip */}
      <Box sx={{ ...panelSx, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, px: 2.5, py: 1.25, mb: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Switch checked={state.wizardData.isActive !== false} onChange={(e) => actions.updateWizardData({ isActive: e.target.checked })} inputProps={{ "aria-label": "Active" }} />
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: state.wizardData.isActive !== false ? "#166534" : t.text2 }}>
            {state.wizardData.isActive !== false ? "Active" : "Paused"}
          </Typography>
        </Box>
        <TextField
          placeholder="Name this automation"
          value={state.wizardData.name}
          onChange={(e) => actions.updateWizardData({ name: e.target.value })}
          inputProps={{ "aria-label": "Automation name" }}
          sx={{ ...inputSx, width: { xs: "100%", sm: 280 } }}
        />
        <Typography sx={{ fontSize: 14, color: t.text2 }}>{editedBy}</Typography>
        {stats && (
          <Box sx={{ ml: "auto", display: "flex", gap: 2.5, flexWrap: "wrap" }}>
            {[
              ["Runs, 30 days", (stats.total_executions || 0).toLocaleString()],
              ["Success", `${stats.total_executions ? stats.success_rate : 0}%`],
              ["Avg time", stats.avg_execution_time_ms ? `${(stats.avg_execution_time_ms / 1000).toFixed(1)} s` : "—"],
            ].map(([k, v]) => (
              <Typography key={k} sx={{ fontSize: 14, color: t.text2 }}>
                {k} <Box component="b" sx={{ color: t.text }}>{v}</Box>
              </Typography>
            ))}
          </Box>
        )}
      </Box>

      <Box sx={{ display: "grid", gap: 2.5, gridTemplateColumns: { xs: "1fr", lg: "240px minmax(0,1fr) 380px" }, alignItems: "start" }}>
        <Box sx={{ ...panelSx, p: 1.75, position: { lg: "sticky" }, top: { lg: 8 } }}>
          <Palette engineReady={engineReady} onTrigger={pickTrigger} onCondition={() => setSelected("conditions")} onAdd={(spec) => addAt(spec, steps.length)} />
        </Box>

        {/* Canvas */}
        <CanvasViewport
          onDropAnywhere={(payload) => handleDrop(payload, steps.length, [])}
          sx={{ ...panelSx, height: { xs: 520, lg: "max(520px, calc(100vh - var(--v2-frame-offset, 116px) - 200px))" } }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", px: 2, pb: 4 }}>
            <FlowCard
              icon={isScheduled ? CalendarClock : Zap}
              tone={TONES.trigger}
              kindLabel="Trigger"
              title={describeTrigger(objectName, state.wizardData.trigger)}
              sub={isScheduled ? describeSchedule(schedule) : state.wizardData.object ? `${objectName}` : "Pick the record type and event"}
              selected={selected === "trigger"}
              invalid={!errorsByStep[0].valid && Boolean(state.showValidationErrors || Object.keys(state.errors || {}).length)}
              onSelect={() => setSelected("trigger")}
            />
            <Connector />
            <FlowCard
              icon={Split}
              tone={TONES.condition}
              kindLabel="Condition"
              title={conditionText.title}
              sub={conditionText.sub}
              selected={selected === "conditions"}
              onSelect={() => setSelected("conditions")}
              badge={
                <Typography sx={{ fontSize: 12, color: t.text3, mt: 0.5 }}>
                  {engineReady
                    ? "Decides whether the automation runs at all. Drag a Condition into the flow to branch."
                    : "Yes/No branches are coming next; for now it decides whether the steps below run."}
                </Typography>
              }
            />
            {renderList(steps, [])}
            <FlowChip>End</FlowChip>
            {!steps.length && (
              <Typography sx={{ mt: 2, fontSize: 14, color: t.text2, textAlign: "center", maxWidth: 360 }}>
                Drag an action from the left onto the + above, or click it to add it at the end.
              </Typography>
            )}
            {trailingWait(steps) && (
              <Alert severity="info" sx={{ mt: 2, maxWidth: 360 }}>
                A wait at the end delays nothing. Add a step after it, or remove it.
              </Alert>
            )}
          </Box>
        </CanvasViewport>

        {/* Inspector */}
        <Box component="aside" aria-label="Step settings" sx={{ ...panelSx, position: { lg: "sticky" }, top: { lg: 8 }, maxHeight: { lg: "calc(100vh - var(--v2-frame-offset, 116px) - 24px)" }, overflowY: "auto" }}>
          {inspectorBody ? (
            <>
              <Box sx={{ px: 2.25, py: 1.75, borderBottom: `1px solid ${t.border}` }}>
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: t.text3 }}>{inspectorKind}</Typography>
                <Typography sx={{ fontSize: 16, fontWeight: 600, color: t.text, mt: 0.25 }}>{inspectorTitle}</Typography>
              </Box>
              {/* The wizard's forms lay out for a wide dialog; in this panel
                  every grid cell takes the full width, and the action form's
                  own card frame is dropped (the panel is the frame). */}
              <Box
                sx={{
                  p: 2.25,
                  "& .MuiGrid-container > .MuiGrid-item": { flexBasis: "100%", maxWidth: "100%" },
                  "& .MuiCard-root": { border: "none", boxShadow: "none", bgcolor: "transparent" },
                  "& .MuiCard-root > .MuiCardContent-root": { p: 0 },
                  "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": { borderColor: t.border2 },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: t.primary },
                }}
              >
                {inspectorBody}
              </Box>
            </>
          ) : (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography sx={{ fontSize: 14, color: t.text2 }}>Select a step to change its settings.</Typography>
            </Box>
          )}
          <SeepEditBox
            enabled={engineReady && canUseAiAssistant && Boolean(state.wizardData.object)}
            disabledReason={
              !engineReady
                ? "Coming next: describe a change and Seep proposes it on the canvas."
                : !canUseAiAssistant
                ? "Needs the AI Assistant add-on and a seat."
                : "Choose the record type first."
            }
            // Always as a flow, with the canvas's ids, so the steps Seep keeps
            // are the same cards.
            buildAutomation={() => ({ ...currentAutomation(), flow: stepsToFlow(mapConditionGroups(steps, (g) => transformService.serializeConditions(g))) })}
            onApply={(proposal) => {
              setSteps(proposal.flow ? mapConditionGroups(flowToSteps(proposal.flow), (g) => transformService.deserializeConditions(g)) : actionsToSteps(proposal.actions));
              actions.setConditionGroups(transformService.deserializeConditions(proposal.trigger_conditions));
              setSelected(null);
              setSnack({ severity: "info", message: "Seep's change is on the canvas. Check it, then Save." });
            }}
          />
        </Box>
      </Box>

      <TestRunDialog
        open={testOpen}
        onClose={() => setTestOpen(false)}
        object={state.wizardData.object}
        objectName={objectName}
        buildBody={() => {
          const a = currentAutomation();
          // Always as a flow, so the path it reports matches the cards.
          return { trigger_object: a.trigger_object, trigger_conditions: a.trigger_conditions, group_logic: a.group_logic, actions: [], flow: stepsToFlow(mapConditionGroups(steps, (g) => transformService.serializeConditions(g))) };
        }}
        onResult={(result) => setTested(new Set((result.path || []).map((p) => p.step_id)))}
      />

      <Snackbar open={Boolean(snack)} autoHideDuration={4000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        {snack ? (
          <Alert severity={snack.severity} variant="filled" onClose={() => setSnack(null)}>
            {snack.message}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>
    </Box>
  );
}

CanvasContent.propTypes = {
  automation: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};
CanvasContent.defaultProps = { automation: null };

export default function AutomationCanvas(props) {
  return (
    <AutomationProvider>
      <CanvasContent {...props} />
    </AutomationProvider>
  );
}
